// src/services/AuthService.ts
import { User } from 'firebase/auth';
import { getApiLang, getAppLanguage } from '../utils/language';
import type { CarInfo } from './CarTripService';
import { fetchWithAuth } from './api/client';

const API_BASE_URL = '/api/v1';

export type UserPreferencesPayload = {
  currency?: string;
  distanceUnit?: string;
  theme?: 'light' | 'dark';
  residenceCountry?: string;
  cars?: CarInfo[];
  selectedCarIndex?: number;
};

export type UserProfile = {
  id: number;
  email: string;
  language: string;
  preferences?: UserPreferencesPayload;
  is_superuser?: boolean;
  isSuperuser?: boolean;
};

type BackendMe = UserProfile;

let lastIsSuperuser = false;

function rememberSuperuser(data: BackendMe | null | undefined) {
  lastIsSuperuser = Boolean(data?.is_superuser ?? data?.isSuperuser);
}

function authHeaders(language?: string): Record<string, string> {
  const lang = getApiLang(language ?? getAppLanguage());
  return {
    'Content-Type': 'application/json',
    'Accept-Language': lang,
  };
}

function authJsonBody<T extends object>(payload: T, language?: string): string {
  const lang = getApiLang(language ?? getAppLanguage());
  return JSON.stringify({ ...payload, lang });
}

/** Map backend UserResponse to Firebase-like User for frontend */
function mapBackendUserToFirebaseUser(data: BackendMe): User {
  rememberSuperuser(data);
  return {
    uid: data.id.toString(),
    email: data.email,
    displayName: data.email.split('@')[0],
    emailVerified: true,
    isAnonymous: false,
    metadata: {},
    providerData: [],
    refreshToken: '',
    tenantId: null,
    delete: () => Promise.resolve(),
    getIdToken: () => Promise.resolve(''),
    getIdTokenResult: () => Promise.resolve({}),
    reload: () => Promise.resolve(),
    toJSON: () => ({}),
    providerId: 'password',
  } as unknown as User;
}

/** Get error message from backend error response (detail can be string or array) */
function getErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === 'object' && 'msg' in first) return (first as { msg: string }).msg;
  }
  return fallback;
}

async function fetchAuthed(
  url: string,
  options?: RequestInit,
  retries = 3,
): Promise<Response> {
  let last: Response | null = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    last = await fetchWithAuth(url, options);
    if (last.status !== 429 || attempt >= retries - 1) {
      return last;
    }
    const retryAfter = Number.parseInt(last.headers.get('Retry-After') || '2', 10);
    await new Promise((r) => setTimeout(r, Math.max(1, retryAfter) * 1000));
  }
  return last!;
}

/**
 * Service for handling authentication operations using the Python backend
 */
export const AuthService = {
  /**
   * Register a new user with email and password
   */
  registerWithEmailAndPassword: async (email: string, password: string, language?: string): Promise<{ user: User }> => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: authHeaders(language),
      body: authJsonBody({ email, password }, language),
      credentials: 'include',
    });

    const errorBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getErrorMessage(errorBody.detail, 'Registration failed'));
    }

    const data = errorBody as { id: number; email: string };
    return { user: mapBackendUserToFirebaseUser(data) };
  },

  /**
   * Sign in with email and password
   */
  signInWithEmailAndPassword: async (email: string, password: string, language?: string): Promise<{ user: User }> => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: authHeaders(language),
      body: authJsonBody({ email, password }, language),
      credentials: 'include',
    });

    const errorBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getErrorMessage(errorBody.detail, 'Login failed'));
    }
    return { user: await AuthService.me() };
  },

  /**
   * Get the current authenticated user profile from the backend
   */
  fetchProfile: async (): Promise<UserProfile | null> => {
    const response = await fetchAuthed(`${API_BASE_URL}/auth/me`);
    if (!response.ok) {
      return null;
    }
    return response.json() as Promise<UserProfile>;
  },

  /**
   * Persist user preferences for the current user (language + UI settings).
   */
  updatePreferences: async (patch: {
    language?: string;
    currency?: string;
    distanceUnit?: string;
    theme?: 'light' | 'dark';
    residenceCountry?: string;
    cars?: CarInfo[];
    selectedCarIndex?: number;
  }): Promise<UserProfile> => {
    const language = patch.language ?? getAppLanguage();
    const response = await fetchAuthed(`${API_BASE_URL}/auth/me`, {
      method: 'PATCH',
      headers: authHeaders(language),
      body: JSON.stringify(patch),
    });
    const errorBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getErrorMessage(errorBody.detail, 'Failed to save settings'));
    }
    return errorBody as UserProfile;
  },

  /** @deprecated use updatePreferences */
  updateLanguage: async (language: string): Promise<UserProfile> => {
    return AuthService.updatePreferences({ language });
  },

  /**
   * Get the current authenticated user from the backend
   */
  me: async (): Promise<User> => {
    const response = await fetchAuthed(`${API_BASE_URL}/auth/me`);

    if (!response.ok) {
      throw new Error('Not authenticated');
    }

    const data = await response.json();
    return mapBackendUserToFirebaseUser(data);
  },

  /**
   * Sign out the current user
   */
  signOut: async (): Promise<void> => {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    // Also clear any local state if needed
    localStorage.removeItem('user');
    lastIsSuperuser = false;
  },

  /**
   * Sign in with Google: передайте id_token, полученный от @react-oauth/google (onSuccess -> credential.credential).
   */
  signInWithGoogle: async (idToken: string): Promise<{ user: User }> => {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
      credentials: 'include',
    });
    const errorBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getErrorMessage(errorBody.detail, 'Google login failed'));
    }
    const data = errorBody as { id: number; email: string };
    return { user: mapBackendUserToFirebaseUser(data) };
  },

  /**
   * Sign in with Apple: передайте id_token (например с страницы /auth/apple-callback после редиректа от Apple).
   */
  signInWithApple: async (idToken: string): Promise<{ user: User }> => {
    const response = await fetch(`${API_BASE_URL}/auth/apple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
      credentials: 'include',
    });
    const errorBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getErrorMessage(errorBody.detail, 'Apple login failed'));
    }
    const data = errorBody as { id: number; email: string };
    return { user: mapBackendUserToFirebaseUser(data) };
  },

  /**
   * Request password reset: send email with link. Backend sends letter if user exists.
   */
  requestPasswordReset: async (email: string, language?: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: authHeaders(language),
      body: authJsonBody({ email }, language),
      credentials: 'include',
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(getErrorMessage(errorBody.detail, 'Request failed'));
    }
  },

  /**
   * Reset password with token from email link.
   */
  resetPassword: async (token: string, newPassword: string, language?: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: authHeaders(language),
      body: authJsonBody({ token, new_password: newPassword }, language),
      credentials: 'include',
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(getErrorMessage(errorBody.detail, 'Reset failed'));
    }
  },

  /**
   * Check if user is authenticated (checking local state or calling me)
   */
  getCurrentUser: async (): Promise<User | null> => {
    try {
      return await AuthService.me();
    } catch (e) {
      lastIsSuperuser = false;
      return null;
    }
  },

  isSuperuser: (): boolean => lastIsSuperuser,
};

export default AuthService;